import React, { useState, useEffect } from 'react'
import { IoIosArrowBack } from 'react-icons/io'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GoBook, GoXCircleFill } from 'react-icons/go'
import { FaCheckCircle, FaTimesCircle, FaRegCalendarAlt, FaExchangeAlt } from 'react-icons/fa'
import api from '../../api/api'
import { useProfileStore } from '../../store/useUserStore'
import { toast } from 'react-toastify'
import { LiaCalendarTimes } from 'react-icons/lia'
import { RiRefreshFill } from 'react-icons/ri'
import { IoIosRefresh } from "react-icons/io";


const AttendanceList: React.FC = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { t } = useTranslation()
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const { setAttendanceData } = useProfileStore((state:any) => state)
    const [students, setStudents] = useState<any[]>([])
    const storeStudents = useProfileStore((state:any) => state.students)

    // Sync local students with store students
    useEffect(() => {
        setStudents(Array.isArray(storeStudents) ? storeStudents : [])
    }, [storeStudents])

    // Check URL params for student_id and restore accordion state
    useEffect(() => {
        const studentIdParam = searchParams.get('student_id')
        if (studentIdParam && students.length > 0) {
            const studentId = parseInt(studentIdParam, 10)
            const studentIndex = students.findIndex(s => s.id === studentId)
            
            if (studentIndex !== -1) {
                setOpenIndex(studentIndex)
                const student = students[studentIndex]
                // Fetch attendance if not already loaded
                if (student && (!student.attendances || student.attendances.length === 0)) {
                    fetchStudentAttendance(student.id)
                }
            }
        }
    }, [searchParams, students.length])

    // local per-student loading state to avoid global page loader
    const [loadingIds, setLoadingIds] = useState<number[]>([])

    const fetchStudentAttendance = async (studentId: number) => {
        const today = new Date()
        const date = today.toISOString().split('T')[0]
        const startDate = '2024-01-01'
        const endDate = date

        try {
            // mark this student as loading (inner loader)
            setLoadingIds(prev => Array.from(new Set([...prev, studentId])))
            const res = await api.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/${date}`, {
                params: {
                    mode: 'student',
                    student_id: studentId,
                    start: startDate,
                    end: endDate,
                }
            })

            const list = res.data?.data || []

            // Follow Flutter logic: keep items that have attendance first, include events as well
            const gotAttn = list.filter((it: any) => it.attendance != null)
            const gotEvent = list.filter((it: any) => it.event != null)

            const uniqueItems: { [key: string]: any } = {}

            for (const item of [...gotAttn, ...gotEvent]) {
                if (item.attendance != null) {
                    const key = String(item.attendance.id)
                    uniqueItems[key] = item
                }
            }

            const combined = Object.values(uniqueItems)

            // sort by course_date descending (newest first)
            combined.sort((a: any, b: any) => {
                const da = a.course_date ? new Date(a.course_date).getTime() : 0
                const db = b.course_date ? new Date(b.course_date).getTime() : 0
                return db - da
            })

            // update local students state with processed attendances
            setStudents(prev => prev.map(s => s.id === studentId ? { ...s, attendances: combined } : s))
        } catch (err) {
            console.error('Failed to fetch student attendance', err)
            toast.error(t('server_error') || 'Server error')
        } finally {
            // remove student from loading set
            setLoadingIds(prev => prev.filter(id => id !== studentId))
        }
    }

    const toggle = (i: number, student: any) => {
        // open/close
        const willOpen = openIndex !== i
        setOpenIndex(willOpen ? i : null)
        
        // Update URL with student_id when opening
        if (willOpen) {
            setSearchParams({ student_id: student.id.toString() })
            // if opening and attendances not loaded, fetch
            if (!student.attendances || student.attendances.length === 0) {
                fetchStudentAttendance(student.id)
            }
        } else {
            // Remove student_id from URL when closing
            setSearchParams({})
        }
    }

     const statusIcon = (attendance: any) => {
        const rawStatus = attendance?.status
        const presentFlag = (attendance && typeof attendance.present !== 'undefined') ? attendance.present : attendance?.attendance?.attend
        const status = rawStatus === 'active' ? (presentFlag === true ? 'present' : 'absent') : (rawStatus ? String(rawStatus).toLowerCase() : 'absent')

        if (status === 'replacement' || status === 'replacement_class' || status === 'replaced' || (attendance?.event && attendance.event.type === 'replacement_student')) {
            return <RiRefreshFill color="blue" size={30} />
        }
        if (status === 'present') return <FaCheckCircle color="#00d3a0" size={30} />
        if (status === 'cancelled' || status === 'canceled') return <LiaCalendarTimes color="red" size={30} />
        return <GoXCircleFill color="red" size={30} />
    }

    const formatCourseDate = (dateStr: any) => {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        if (isNaN(d.getTime())) return String(dateStr)
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    return (
        <div className="pb-28">
            <div className='mb-4 grid grid-cols-4 px-4 pt-2 items-center'>
                <div>
                    <IoIosArrowBack size={28} color='#000' onClick={() => navigate('/inbox')} />
                </div>
                <div className='text-center col-span-2 text-lg font-medium'>{t("attendance.title")}</div>
                <div></div>
            </div>

            <div className="px-4 space-y-4">
                {/* Legend */}
                <div className="bg-gray-100 rounded-xl p-4">
                    <div className="flex justify-around items-center gap-4">
                        <div className="flex-1 flex flex-col items-center justify-center gap-1 py-2">
                            <FaCheckCircle className='text-theme' size={22} />
                            <div className='text-xs font-semibold mt-1'>{t("attendance.PRESENT")}</div>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center gap-1 py-2">
                            <GoXCircleFill className='text-red-500' size={22} />
                            <div className='text-xs font-semibold mt-1'>{t("attendance.ABSENT")}</div>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center gap-1 py-2">
                            <LiaCalendarTimes className='text-gray-600' size={22} />
                            <div className='text-xs font-semibold mt-1'>{t("attendance.CANCELLED")}</div>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center gap-1 py-2">
                            <RiRefreshFill className='text-blue-600' size={22} />
                            <div className='text-xs font-semibold mt-1'>{t("attendance.REPLACEMENT")}</div>
                        </div>
                    </div>
                </div>

                {/* Students accordion */}
                <div className="space-y-4">
                    {students.map((student, idx) => (
                        <div key={student.id} className="bg-white rounded-xl shadow-sm">
                            <button
                                className="w-full text-left px-4 py-4 flex items-center justify-between"
                                onClick={() => toggle(idx, student)}
                                aria-expanded={openIndex === idx}
                            >
                                <div className='font-medium'>{student.name}</div>
                                <div className='text-gray-400'>{openIndex === idx ? '▴' : '▾'}</div>
                            </button>

                            {openIndex === idx && (
                                <div className="px-4 pb-4">
                                    <div className='space-y-3 mt-2'>
                                        {loadingIds.includes(student.id) ? (
                                            <div className='py-4 flex items-center justify-center text-sm text-gray-500'>
                                                <IoIosRefresh className='animate-spin mr-2' size={18} />
                                            </div>
                                        ) : (student.attendances && student.attendances.length > 0 ? (
                                            student.attendances.map((a: any, idx: number) => (
                                                <div key={idx} className="rounded-lg border border-gray-200 p-4 flex items-start justify-between bg-white cursor-pointer"
                                                    onClick={() => {
                                                        if (a?.attendance?.id) {
                                                            setAttendanceData({
                                                                studentName: student?.name,
                                                                studentId: student.id,
                                                                eventId: a?.event?.id,
                                                            })
                                                            console.log(student?.name, student.id)
                                                            navigate(`/attendance/${a.attendance.id}`)
                                                        }
                                                    }}
                                                >
                                                    <div className='flex items-start gap-4'>
                                                        <div className='mt-1'>
                                                            {statusIcon(a)}
                                                        </div>
                                                        <div>
                                                            <div className='text-sm font-semibold'>{a.course_name}</div>
                                                            <div className='text-xs text-gray-400 mt-2'>{a.start_time} - {a.end_time}</div>
                                                        </div>
                                                    </div>
                                                    <div className='text-sm text-gray-700 font-medium'>{formatCourseDate(a.course_date)}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className='text-gray-500 py-4 text-sm'>{t("attendance.no_attendance_records")}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

            </div>
        </div>
    )
}

export default AttendanceList